import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

// The caller's access level on a table, shown as a text label on the table's schema
// row: "Read/Write" for write access, "Read-only" for read access. Nothing is shown
// when the API does not annotate an access level for the table.
//
// Write access to a view is contradictory (views are inherently read-only), so in that
// case the label and an info icon share a single explanatory tooltip.
const TableAccessLabel = ({
  accessLevel,
  isView = false,
}: {
  accessLevel?: DatasetTableResponse["accessLevel"];
  isView?: boolean;
}) => {
  if (!accessLevel) {
    return null;
  }
  const isWrite = accessLevel === "write";

  if (isWrite && isView) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="table-access-label" className="inline-flex items-center gap-0.5">
            Read/Write
            <Info data-testid="view-write-info" className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          You have been granted read/write access, but this is a view. Views are inherently
          read-only.
        </TooltipContent>
      </Tooltip>
    );
  }

  const label = isWrite ? "Read/Write" : "Read-only";
  const tooltip = isWrite
    ? "You have read/write access to this table."
    : "You have read-only access to this table.";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-testid="table-access-label">{label}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export default TableAccessLabel;
